// FILE: apps/admin/src/components/Icons.tsx
// VERSION: 1.0.0
// START_MODULE_CONTRACT
//   PURPOSE: Unified crisp inline SVG icon library for the Admin Panel including official Telegram Stars, Telegram Premium, and GRAM vectors plus Tamagotchi and operator UI icons.
//   SCOPE: AdminIconName, AdminIcon component, CompanionIcon helper, and inline alignment wrapper.
//   DEPENDS: none
//   LINKS: M-ADMIN
//   ROLE: RUNTIME
//   MAP_MODE: EXPORTS
// END_MODULE_CONTRACT
//
// START_MODULE_MAP
//   AdminIcon - Render crisp inline SVG icon by semantic name (official Stars, GRAM, Premium, fish, and UI icons)
//   AdminIconName - Union of supported Admin SVG icon names
//   CompanionIcon - Render Tamagotchi fish species SVG icon by companion ID (clown, fugu, shark, koi)
// END_MODULE_MAP

import { type CSSProperties, useId } from 'react';

export type AdminIconName =
  | 'star'
  | 'premium'
  | 'gram'
  | 'gramMark'
  | 'clown'
  | 'fugu'
  | 'shark'
  | 'koi'
  | 'lock'
  | 'shield'
  | 'logout'
  | 'check'
  | 'close'
  | 'ban'
  | 'skull'
  | 'bell'
  | 'bolt'
  | 'refresh'
  | 'send'
  | 'food'
  | 'sparkle'
  | 'heart'
  | 'play'
  | 'pause'
  | 'verified'
  | 'globe'
  | 'device'
  | 'copy'
  | 'flag'
  | 'calendar'
  | 'reset'
  | 'gear'
  | 'tag'
  | 'plus'
  | 'user'
  | 'search';

// START_CONTRACT: AdminIcon
//   PURPOSE: Render crisp vector SVG icons for currencies (official Telegram Stars, GRAM, Premium), Tamagotchi pets, and operator controls without using Unicode emojis.
//   INPUTS: name: AdminIconName, size?: number, style?: CSSProperties, className?: string
//   OUTPUTS: React SVG span wrapper
//   SIDE_EFFECTS: none
//   LINKS: M-ADMIN
// END_CONTRACT: AdminIcon
export function AdminIcon({
  name,
  size = 15,
  style,
  className,
}: {
  name: AdminIconName;
  size?: number;
  style?: CSSProperties;
  className?: string;
}) {
  // START_BLOCK_ADMIN_ICON
  const uid = useId().replace(/:/g, '');
  const wrapStyle: CSSProperties = {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: size,
    height: size,
    flexShrink: 0,
    verticalAlign: '-0.16em',
    lineHeight: 0,
    ...style,
  };

  switch (name) {
    case 'star': {
      const gradId = `admStar_${uid}`;
      return (
        <span className={className} style={wrapStyle} aria-hidden="true">
          <svg viewBox="0 0 24 24" width={size} height={size}>
            <defs>
              <linearGradient id={gradId} x1="0.2" y1="0" x2="0.8" y2="1">
                <stop offset="0%" stopColor="#ffe07a" />
                <stop offset="50%" stopColor="#fcbc1d" />
                <stop offset="100%" stopColor="#f59e0b" />
              </linearGradient>
            </defs>
            <path
              fill={`url(#${gradId})`}
              d="m11.47 18.6-4.5 2.73a1 1 0 0 1-1.37-.32.98.98 0 0 1-.12-.76l.7-2.73c.25-.98.93-1.8 1.85-2.24l4.91-2.34a.46.46 0 0 0 .22-.61.46.46 0 0 0-.5-.25l-5.46.94a3.85 3.85 0 0 1-3.12-.84l-1.72-1.43A.98.98 0 0 1 2.92 9l5.27-.41a1 1 0 0 0 .84-.61l2.04-4.88a1 1 0 0 1 1.83 0l2.04 4.88a1 1 0 0 0 .84.6l5.3.42a.99.99 0 0 1 .57 1.73l-4.04 3.43a.98.98 0 0 0-.33.98l1.25 5.13a.99.99 0 0 1-.74 1.2 1 1 0 0 1-.75-.13L12.5 18.6a1 1 0 0 0-1.03 0Z"
            />
          </svg>
        </span>
      );
    }

    case 'premium': {
      const gradId = `admPrem_${uid}`;
      return (
        <span className={className} style={wrapStyle} aria-hidden="true">
          <svg viewBox="0 0 24 24" width={size} height={size}>
            <defs>
              <linearGradient id={gradId} x1="0.2" y1="0" x2="0.8" y2="1">
                <stop offset="0%" stopColor="#b794ff" />
                <stop offset="50%" stopColor="#8b5cf6" />
                <stop offset="100%" stopColor="#e24bd0" />
              </linearGradient>
            </defs>
            <path
              fill={`url(#${gradId})`}
              d="m11.47 18.6-4.5 2.73a1 1 0 0 1-1.37-.32.98.98 0 0 1-.12-.76l.7-2.73c.25-.98.93-1.8 1.85-2.24l4.91-2.34a.46.46 0 0 0 .22-.61.46.46 0 0 0-.5-.25l-5.46.94a3.85 3.85 0 0 1-3.12-.84l-1.72-1.43A.98.98 0 0 1 2.92 9l5.27-.41a1 1 0 0 0 .84-.61l2.04-4.88a1 1 0 0 1 1.83 0l2.04 4.88a1 1 0 0 0 .84.6l5.3.42a.99.99 0 0 1 .57 1.73l-4.04 3.43a.98.98 0 0 0-.33.98l1.25 5.13a.99.99 0 0 1-.74 1.2 1 1 0 0 1-.75-.13L12.5 18.6a1 1 0 0 0-1.03 0Z"
            />
          </svg>
        </span>
      );
    }

    case 'gram':
      return (
        <span className={className} style={wrapStyle} aria-hidden="true">
          <svg viewBox="0 0 100 100" width={size} height={size} fill="none">
            <rect width="100" height="100" rx="50" fill="#30A1F5" />
            <path
              d="M60.41 26.75H39.59c-2.772 0-4.159 0-5.413.388a8.691 8.691 0 0 0-3.028 1.653c-1.005.846-1.754 2.012-3.254 4.344L21.277 43.43c-.99 1.54-1.486 2.311-1.62 3.122-.119.715-.04 1.45.228 2.123.304.764.951 1.411 2.247 2.707l24.59 24.59c1.148 1.148 1.721 1.722 2.383 1.936.582.19 1.208.19 1.79 0 .661-.214 1.235-.788 2.382-1.935l24.591-24.591c1.296-1.296 1.943-1.943 2.247-2.707a3.982 3.982 0 0 0 .228-2.123c-.134-.81-.63-1.581-1.62-3.122l-6.618-10.295c-1.5-2.332-2.25-3.498-3.254-4.344a8.692 8.692 0 0 0-3.028-1.653c-1.255-.388-2.64-.388-5.414-.388z"
              fill="#fff"
            />
            <path
              d="M56.469 34.871c.338-.914 1.631-.914 1.97 0l2.337 6.317c.14.38.44.679.819.82l6.317 2.337c.914.338.914 1.63 0 1.97l-6.317 2.337c-.38.14-.679.44-.82.818l-2.337 6.317c-.338.915-1.631.915-1.97 0l-2.337-6.317c-.14-.379-.44-.678-.819-.818l-6.316-2.338c-.915-.338-.915-1.631 0-1.97l6.316-2.337c.38-.14.679-.44.82-.819l2.337-6.317z"
              fill="#30A1F5"
            />
          </svg>
        </span>
      );

    case 'gramMark':
      return (
        <span className={className} style={wrapStyle} aria-hidden="true">
          <svg viewBox="0 0 100 100" width={size} height={size} fill="none">
            <path
              d="M66.523 11.333H33.477c-4.401 0-6.601 0-8.592.616a13.792 13.792 0 0 0-4.808 2.625c-1.594 1.341-2.784 3.192-5.164 6.894L4.408 37.81c-1.572 2.446-2.358 3.67-2.572 4.956a6.322 6.322 0 0 0 .362 3.37c.482 1.212 1.51 2.24 3.567 4.296l39.033 39.034c1.821 1.82 2.731 2.731 3.781 3.072.924.3 1.918.3 2.842 0 1.05-.34 1.96-1.251 3.78-3.072l39.035-39.034c2.056-2.056 3.084-3.084 3.566-4.296a6.32 6.32 0 0 0 .362-3.37c-.214-1.287-1-2.51-2.572-4.956L85.087 21.47c-2.38-3.703-3.57-5.554-5.164-6.895a13.792 13.792 0 0 0-4.808-2.625c-1.99-.616-4.191-.616-8.592-.616z"
              fill="#30A1F5"
            />
            <path
              d="M60.268 24.224c.537-1.45 2.59-1.45 3.126 0l3.71 10.027a2.2 2.2 0 0 0 1.3 1.3l10.027 3.71c1.451.537 1.451 2.59 0 3.126l-10.027 3.71a2.2 2.2 0 0 0-1.3 1.3l-3.71 10.027c-.537 1.451-2.59 1.451-3.126 0l-3.71-10.027a2.2 2.2 0 0 0-1.3-1.3l-10.027-3.71c-1.451-.537-1.451-2.589 0-3.126l10.027-3.71a2.2 2.2 0 0 0 1.3-1.3l3.71-10.027z"
              fill="#fff"
            />
          </svg>
        </span>
      );

    case 'clown': {
      const gradId = `admFish_${uid}`;
      return (
        <span className={className} style={wrapStyle} aria-hidden="true">
          <svg viewBox="0 0 24 24" width={size} height={size}>
            <defs>
              <linearGradient id={gradId} x1="0" y1="0" x2="1" y2="1">
                <stop offset="0%" stopColor="#ff9e44" />
                <stop offset="100%" stopColor="#f43f5e" />
              </linearGradient>
            </defs>
            <path
              d="M18.5 12c0 3.6-3.4 6.2-7.5 6.2-2.4 0-4.5-.9-5.8-2.3L2 18v-12l3.2 2.1C6.5 6.7 8.6 5.8 11 5.8c4.1 0 7.5 2.6 7.5 6.2Z"
              fill={`url(#${gradId})`}
            />
            <path d="M10.5 6v12M14 6.8v10.4" stroke="#fff" strokeWidth="1.8" strokeLinecap="round" opacity="0.85" />
            <circle cx="15.8" cy="10.8" r="1.2" fill="#0f172a" />
          </svg>
        </span>
      );
    }

    case 'fugu': {
      const gradId = `admFugu_${uid}`;
      return (
        <span className={className} style={wrapStyle} aria-hidden="true">
          <svg viewBox="0 0 24 24" width={size} height={size}>
            <defs>
              <radialGradient id={gradId} cx="0.4" cy="0.35" r="0.7">
                <stop offset="0%" stopColor="#7dd3fc" />
                <stop offset="100%" stopColor="#0284c7" />
              </radialGradient>
            </defs>
            <circle cx="12" cy="12" r="7.4" fill={`url(#${gradId})`} stroke="#bae6fd" strokeWidth="1.2" />
            <path
              d="M12 3v1.8M12 19.2V21M3 12h1.8M19.2 12H21M5.6 5.6l1.3 1.3M17.1 17.1l1.3 1.3M18.4 5.6l-1.3 1.3M6.9 17.1l-1.3 1.3"
              stroke="#7dd3fc"
              strokeWidth="1.8"
              strokeLinecap="round"
            />
            <circle cx="9.6" cy="11" r="1.2" fill="#082f49" />
            <circle cx="14.4" cy="11" r="1.2" fill="#082f49" />
          </svg>
        </span>
      );
    }

    case 'shark': {
      const gradId = `admShark_${uid}`;
      return (
        <span className={className} style={wrapStyle} aria-hidden="true">
          <svg viewBox="0 0 24 24" width={size} height={size}>
            <defs>
              <linearGradient id={gradId} x1="0" y1="0" x2="1" y2="1">
                <stop offset="0%" stopColor="#38bdf8" />
                <stop offset="100%" stopColor="#6366f1" />
              </linearGradient>
            </defs>
            <path
              d="M3 16.5c2.5-1.2 4.8-3.8 6.2-9.5 3.6 1.8 5.6 4.8 6.3 7.8 2-.2 3.8.4 5.5 1.7-4.2 1.8-12.5 1.8-18 0Z"
              fill={`url(#${gradId})`}
              stroke="#bae6fd"
              strokeWidth="1.2"
              strokeLinejoin="round"
            />
            <circle cx="13.5" cy="13.5" r="1.1" fill="#090d16" />
          </svg>
        </span>
      );
    }

    case 'koi': {
      const gradId = `admKoi_${uid}`;
      return (
        <span className={className} style={wrapStyle} aria-hidden="true">
          <svg viewBox="0 0 24 24" width={size} height={size}>
            <defs>
              <linearGradient id={gradId} x1="0" y1="0" x2="1" y2="1">
                <stop offset="0%" stopColor="#fde047" />
                <stop offset="50%" stopColor="#f59e0b" />
                <stop offset="100%" stopColor="#ec4899" />
              </linearGradient>
            </defs>
            <path
              d="M19 12c0 3.6-3.5 6.4-7.8 6.4-2.5 0-4.6-.9-6-2.4L2 18.2V5.8L5.2 8c1.4-1.5 3.5-2.4 6-2.4C15.5 5.6 19 8.4 19 12Z"
              fill={`url(#${gradId})`}
              stroke="#fef08a"
              strokeWidth="1.1"
            />
            <circle cx="15.5" cy="10.8" r="1.2" fill="#451a03" />
          </svg>
        </span>
      );
    }

    case 'lock':
      return (
        <span className={className} style={wrapStyle} aria-hidden="true">
          <svg viewBox="0 0 24 24" width={size} height={size} fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <rect x="4" y="11" width="16" height="10" rx="2" />
            <path d="M8 11V7a4 4 0 0 1 8 0v4" />
          </svg>
        </span>
      );

    case 'shield':
      return (
        <span className={className} style={wrapStyle} aria-hidden="true">
          <svg viewBox="0 0 24 24" width={size} height={size} fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
          </svg>
        </span>
      );

    case 'logout':
      return (
        <span className={className} style={wrapStyle} aria-hidden="true">
          <svg viewBox="0 0 24 24" width={size} height={size} fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
            <polyline points="16 17 21 12 16 7" />
            <line x1="21" y1="12" x2="9" y2="12" />
          </svg>
        </span>
      );

    case 'check':
      return (
        <span className={className} style={wrapStyle} aria-hidden="true">
          <svg viewBox="0 0 24 24" width={size} height={size} fill="none" stroke="currentColor" strokeWidth="2.3" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="20 6 9 17 4 12" />
          </svg>
        </span>
      );

    case 'close':
      return (
        <span className={className} style={wrapStyle} aria-hidden="true">
          <svg viewBox="0 0 24 24" width={size} height={size} fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </span>
      );

    case 'ban':
      return (
        <span className={className} style={wrapStyle} aria-hidden="true">
          <svg viewBox="0 0 24 24" width={size} height={size} fill="none" stroke="currentColor" strokeWidth="2.1" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="9" />
            <line x1="5.7" y1="5.7" x2="18.3" y2="18.3" />
          </svg>
        </span>
      );

    case 'skull':
      return (
        <span className={className} style={wrapStyle} aria-hidden="true">
          <svg viewBox="0 0 24 24" width={size} height={size} fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M12 3a8 8 0 0 0-8 8c0 2.8 1.5 5.2 3.7 6.6V20a1 1 0 0 0 1 1h6.6a1 1 0 0 0 1-1v-2.4A7.95 7.95 0 0 0 20 11a8 8 0 0 0-8-8Z" />
            <circle cx="9.2" cy="11.5" r="1.2" fill="currentColor" />
            <circle cx="14.8" cy="11.5" r="1.2" fill="currentColor" />
          </svg>
        </span>
      );

    case 'bell':
      return (
        <span className={className} style={wrapStyle} aria-hidden="true">
          <svg viewBox="0 0 24 24" width={size} height={size} fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
            <path d="M13.73 21a2 2 0 0 1-3.46 0" />
          </svg>
        </span>
      );

    case 'bolt':
      return (
        <span className={className} style={wrapStyle} aria-hidden="true">
          <svg viewBox="0 0 24 24" width={size} height={size} fill="none" stroke="currentColor" strokeWidth="2.1" strokeLinecap="round" strokeLinejoin="round">
            <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" />
          </svg>
        </span>
      );

    case 'refresh':
      return (
        <span className={className} style={wrapStyle} aria-hidden="true">
          <svg viewBox="0 0 24 24" width={size} height={size} fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="23 4 23 10 17 10" />
            <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10" />
          </svg>
        </span>
      );

    case 'send':
      return (
        <span className={className} style={wrapStyle} aria-hidden="true">
          <svg viewBox="0 0 24 24" width={size} height={size} fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <line x1="22" y1="2" x2="11" y2="13" />
            <polygon points="22 2 15 22 11 13 2 9 22 2" />
          </svg>
        </span>
      );

    case 'food':
      return (
        <span className={className} style={wrapStyle} aria-hidden="true">
          <svg viewBox="0 0 24 24" width={size} height={size} fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M15.5 5.5c3.5 1 5.5 4.5 4.2 8.2-1.2 3.3-4.6 5.3-8.1 4.6-2.4-.5-4.1-2.2-4.1-4.1 0-1.6 1.3-2.6 2.7-2.3.9.2 1.5 1 2.4.8 1.1-.2 1.6-1.5 1-2.6-.7-1.2-2.5-1.5-4.2-.8-1.4-2 1.6-4.9 6.1-3.8Z" />
          </svg>
        </span>
      );

    case 'sparkle':
      return (
        <span className={className} style={wrapStyle} aria-hidden="true">
          <svg viewBox="0 0 24 24" width={size} height={size} fill="currentColor">
            <path d="M12 2.5 14.4 9l6.6 2.4-6.6 2.4L12 20.5 9.6 13.8 3 11.4 9.6 9 12 2.5Z" />
          </svg>
        </span>
      );

    case 'heart':
      return (
        <span className={className} style={wrapStyle} aria-hidden="true">
          <svg viewBox="0 0 24 24" width={size} height={size} fill="currentColor">
            <path d="M12 20.4 4.9 13.5a4.8 4.8 0 0 1 6.8-6.8l.3.3.3-.3a4.8 4.8 0 0 1 6.8 6.8L12 20.4Z" />
          </svg>
        </span>
      );

    case 'play':
      return (
        <span className={className} style={wrapStyle} aria-hidden="true">
          <svg viewBox="0 0 24 24" width={size} height={size} fill="currentColor">
            <polygon points="6 4 20 12 6 20 6 4" />
          </svg>
        </span>
      );

    case 'pause':
      return (
        <span className={className} style={wrapStyle} aria-hidden="true">
          <svg viewBox="0 0 24 24" width={size} height={size} fill="currentColor">
            <rect x="6" y="4" width="4" height="16" rx="1" />
            <rect x="14" y="4" width="4" height="16" rx="1" />
          </svg>
        </span>
      );

    case 'verified':
      return (
        <span className={className} style={wrapStyle} aria-hidden="true">
          <svg viewBox="0 0 24 24" width={size} height={size} fill="none">
            <path
              d="M12 2.25l2.22 1.84 2.86-.32 1.15 2.64 2.64 1.15-.32 2.86 1.84 2.22-1.84 2.22.32 2.86-2.64 1.15-1.15 2.64-2.86-.32L12 23.03l-2.22-1.84-2.86.32-1.15-2.64-2.64-1.15.32-2.86L1.61 12.64l1.84-2.22-.32-2.86 2.64-1.15 1.15-2.64 2.86.32L12 2.25z"
              fill="#38bdf8"
            />
            <path
              d="M8.5 12.6l2.35 2.35 4.85-4.95"
              stroke="#082f49"
              strokeWidth="2.3"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </span>
      );

    case 'globe':
      return (
        <span className={className} style={wrapStyle} aria-hidden="true">
          <svg viewBox="0 0 24 24" width={size} height={size} fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="9.5" />
            <line x1="2.5" y1="12" x2="21.5" y2="12" />
            <path d="M12 2.5a14.5 14.5 0 0 1 0 19 14.5 14.5 0 0 1 0-19" />
          </svg>
        </span>
      );

    case 'device':
      return (
        <span className={className} style={wrapStyle} aria-hidden="true">
          <svg viewBox="0 0 24 24" width={size} height={size} fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
            <rect x="6" y="2.5" width="12" height="19" rx="2.5" />
            <line x1="11" y1="18" x2="13" y2="18" />
          </svg>
        </span>
      );

    case 'copy':
      return (
        <span className={className} style={wrapStyle} aria-hidden="true">
          <svg viewBox="0 0 24 24" width={size} height={size} fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
            <rect x="9" y="9" width="12" height="12" rx="2" />
            <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
          </svg>
        </span>
      );

    case 'flag':
      return (
        <span className={className} style={wrapStyle} aria-hidden="true">
          <svg viewBox="0 0 24 24" width={size} height={size} fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M4 15s1-1 4-1 5 2 8 2 4-1 4-1V3s-1 1-4 1-5-2-8-2-4 1-4 1z" />
            <line x1="4" y1="22" x2="4" y2="15" />
          </svg>
        </span>
      );

    case 'calendar':
      return (
        <span className={className} style={wrapStyle} aria-hidden="true">
          <svg viewBox="0 0 24 24" width={size} height={size} fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
            <rect x="3" y="4" width="18" height="17" rx="2" />
            <line x1="16" y1="2.5" x2="16" y2="6" />
            <line x1="8" y1="2.5" x2="8" y2="6" />
            <line x1="3" y1="9.5" x2="21" y2="9.5" />
          </svg>
        </span>
      );

    case 'reset':
      return (
        <span className={className} style={wrapStyle} aria-hidden="true">
          <svg viewBox="0 0 24 24" width={size} height={size} fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M3 12a9 9 0 1 0 3-6.7L3 8" />
            <polyline points="3 3 3 8 8 8" />
          </svg>
        </span>
      );

    case 'gear':
      return (
        <span className={className} style={wrapStyle} aria-hidden="true">
          <svg viewBox="0 0 24 24" width={size} height={size} fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="3" />
            <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
          </svg>
        </span>
      );

    case 'tag':
      return (
        <span className={className} style={wrapStyle} aria-hidden="true">
          <svg viewBox="0 0 24 24" width={size} height={size} fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
            <path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z" />
            <line x1="7" y1="7" x2="7.01" y2="7" />
          </svg>
        </span>
      );

    case 'plus':
      return (
        <span className={className} style={wrapStyle} aria-hidden="true">
          <svg viewBox="0 0 24 24" width={size} height={size} fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
            <line x1="12" y1="5" x2="12" y2="19" />
            <line x1="5" y1="12" x2="19" y2="12" />
          </svg>
        </span>
      );

    case 'user':
      return (
        <span className={className} style={wrapStyle} aria-hidden="true">
          <svg viewBox="0 0 24 24" width={size} height={size} fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
            <circle cx="12" cy="7" r="4" />
          </svg>
        </span>
      );

    case 'search':
      return (
        <span className={className} style={wrapStyle} aria-hidden="true">
          <svg viewBox="0 0 24 24" width={size} height={size} fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="11" cy="11" r="8" />
            <line x1="21" y1="21" x2="16.65" y2="16.65" />
          </svg>
        </span>
      );
  }
  // END_BLOCK_ADMIN_ICON
}

// START_CONTRACT: CompanionIcon
//   PURPOSE: Map a Tamagotchi companion ID (clown, fugu, shark, koi, or legacy alias) to its matching SVG vector icon.
//   INPUTS: companionId?: string, size?: number
//   OUTPUTS: React element
//   SIDE_EFFECTS: none
//   LINKS: M-ADMIN
// END_CONTRACT: CompanionIcon
export function CompanionIcon({
  companionId,
  size = 18,
}: {
  companionId?: string;
  size?: number;
}) {
  // START_BLOCK_COMPANION_ICON
  const normalized =
    companionId === 'fugu' || companionId === 'coffee'
      ? 'fugu'
      : companionId === 'shark' || companionId === 'croc'
        ? 'shark'
        : companionId === 'koi' || companionId === 'star'
          ? 'koi'
          : 'clown';
  return <AdminIcon name={normalized} size={size} />;
  // END_BLOCK_COMPANION_ICON
}
